/**
 * Shared file upload logic.
 *
 * Centralises the upload-and-create-node pipeline used by Canvas.svelte
 * (drag-and-drop, clipboard paste).
 *
 * Shows a toast while uploading and dismisses it on completion or error.
 */

import type { RoolChannel } from '@rool-dev/sdk';
import { isPdf, pdfFirstPageToBlob } from './pdf';
import * as toast from './toast';

// ---------------------------------------------------------------------------
// Public helpers
// ---------------------------------------------------------------------------

/** Check if a file is a markdown file */
export function isMarkdownFile(file: File): boolean {
	return (
		file.name.toLowerCase().endsWith('.md') ||
		file.name.toLowerCase().endsWith('.markdown') ||
		file.type === 'text/markdown' ||
		file.type === 'text/x-markdown'
	);
}

/** Check if a file is a docx file */
export function isDocxFile(file: File): boolean {
	return (
		file.name.toLowerCase().endsWith('.docx') ||
		file.type ===
			'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
	);
}

/** Check if a file is a supported type for upload */
export function isSupportedFile(file: File): boolean {
	return (
		file.type.startsWith('image/') ||
		isPdf(file) ||
		isMarkdownFile(file) ||
		isDocxFile(file)
	);
}

// ---------------------------------------------------------------------------
// Sizing constants & helpers
// ---------------------------------------------------------------------------

const IMAGE_MAX_WIDTH = 400;
const IMAGE_MAX_HEIGHT = 300;
const IMAGE_MIN_WIDTH = 180;
const IMAGE_MIN_HEIGHT = 120;
const IMAGE_FALLBACK = { width: 300, height: 300 };
const MARKDOWN_SIZE = { width: 320, height: 220 };
const STAGGER_OFFSET = 20;

/** Compute aspect-ratio-preserving node size for an image */
export function computeImageSize(
	naturalWidth: number,
	naturalHeight: number,
): { width: number; height: number } {
	const aspect = naturalWidth / naturalHeight;
	let width = IMAGE_MAX_WIDTH;
	let height = width / aspect;

	if (height > IMAGE_MAX_HEIGHT) {
		height = IMAGE_MAX_HEIGHT;
		width = height * aspect;
	}

	width = Math.max(IMAGE_MIN_WIDTH, Math.round(width));
	height = Math.max(IMAGE_MIN_HEIGHT, Math.round(height));
	return { width, height };
}

/** Read actual dimensions from an image File using a local object URL */
export function getFileDimensions(
	file: File,
): Promise<{ width: number; height: number } | null> {
	return new Promise((resolve) => {
		const url = URL.createObjectURL(file);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve({ width: img.naturalWidth, height: img.naturalHeight });
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			resolve(null);
		};
		img.src = url;
	});
}

/** Read actual dimensions from a Blob (rendered thumbnail) */
export function getBlobDimensions(
	blob: Blob,
): Promise<{ width: number; height: number } | null> {
	return new Promise((resolve) => {
		const url = URL.createObjectURL(blob);
		const img = new Image();
		img.onload = () => {
			URL.revokeObjectURL(url);
			resolve({ width: img.naturalWidth, height: img.naturalHeight });
		};
		img.onerror = () => {
			URL.revokeObjectURL(url);
			resolve(null);
		};
		img.src = url;
	});
}

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface UploadResult {
	objectId: string;
}

// ---------------------------------------------------------------------------
// Core upload-and-create pipeline
// ---------------------------------------------------------------------------

/**
 * Upload files and create canvas nodes.
 *
 * @param space       Active RoolChannel
 * @param files       Raw File list (will be filtered to supported types)
 * @param position    Canvas position (centre) where nodes are placed
 * @param checkpoint  Undo checkpoint label
 * @returns           Array of created object IDs
 */
export async function uploadFiles(
	space: RoolChannel,
	files: File[],
	position: { x: number; y: number },
	checkpoint: string,
): Promise<string[]> {
	const supported = files.filter(isSupportedFile);
	const unsupported = files.filter((f) => !isSupportedFile(f));

	if (unsupported.length > 0) {
		const names = unsupported.map((f) => f.name);
		toast.show('Unsupported file type', {
			detail: names.length === 1 ? names[0] : `${names.length} files`,
		});
	}

	if (supported.length === 0) return [];

	space.checkpoint(checkpoint);

	const toastId =
		supported.length === 1
			? toast.show('Uploading', { detail: supported[0].name, duration: 0 })
			: toast.show('Uploading', {
					detail: `${supported.length} files`,
					duration: 0,
				});

	const createdIds: string[] = [];

	try {
		for (let i = 0; i < supported.length; i++) {
			const file = supported[i];
			try {
				if (supported.length > 1) {
					toast.update(toastId, {
						message: `Uploading ${i + 1} of ${supported.length}`,
						detail: file.name,
					});
				}

				const id = await uploadSingleFile(space, file, position, i);
				createdIds.push(id);
			} catch (err) {
				console.error(`[file-upload] Failed to upload ${file.name}:`, err);
			}
		}

		// Success feedback
		toast.update(toastId, {
			message:
				createdIds.length === 1
					? 'Upload complete'
					: `${createdIds.length} files uploaded`,
			detail: undefined,
			duration: 2000,
		});
	} catch (err) {
		toast.update(toastId, {
			message: 'Upload failed',
			detail: undefined,
			duration: 3000,
		});
	}

	return createdIds;
}

// ---------------------------------------------------------------------------
// Single-file upload (internal)
// ---------------------------------------------------------------------------

async function uploadSingleFile(
	space: RoolChannel,
	file: File,
	centre: { x: number; y: number },
	index: number,
): Promise<string> {
	if (isMarkdownFile(file)) {
		return uploadMarkdown(space, file, centre, index);
	}
	if (isDocxFile(file)) {
		return uploadDocx(space, file, centre, index);
	}
	if (isPdf(file)) {
		return uploadPdf(space, file, centre, index);
	}
	return uploadImage(space, file, centre, index);
}

// -- Markdown (no media upload, just read text) ------------------------------

async function uploadMarkdown(
	space: RoolChannel,
	file: File,
	centre: { x: number; y: number },
	index: number,
): Promise<string> {
	const content = await file.text();
	const x = centre.x - MARKDOWN_SIZE.width / 2 + index * STAGGER_OFFSET;
	const y = centre.y - MARKDOWN_SIZE.height / 2 + index * STAGGER_OFFSET;

	const result = await space.createObject({
		data: {
			type: 'markdown',
			content,
			title: file.name.replace(/\.(md|markdown)$/i, ''),
			_x: x,
			_y: y,
			_width: MARKDOWN_SIZE.width,
			_height: MARKDOWN_SIZE.height,
		},
	});
	return result.object.id;
}

// -- DOCX (upload file, icon-card node — no thumbnail) ----------------------

const FILE_NODE_SIZE = { width: 200, height: 200 };

async function uploadDocx(
	space: RoolChannel,
	file: File,
	centre: { x: number; y: number },
	index: number,
): Promise<string> {
	const fileUrl = await space.uploadMedia(file);

	const x = centre.x - FILE_NODE_SIZE.width / 2 + index * STAGGER_OFFSET;
	const y = centre.y - FILE_NODE_SIZE.height / 2 + index * STAGGER_OFFSET;

	const result = await space.createObject({
		data: {
			type: 'file',
			url: '',
			fileName: file.name,
			fileType: file.type,
			sourceFile: fileUrl,
			sourceFileName: file.name,
			sourceFileType: file.type,
			title: file.name.replace(/\.docx$/i, ''),
			_x: x,
			_y: y,
			_width: FILE_NODE_SIZE.width,
			_height: FILE_NODE_SIZE.height,
		},
	});
	return result.object.id;
}

// -- PDF (upload file + thumbnail) -------------------------------------------

async function uploadPdf(
	space: RoolChannel,
	file: File,
	centre: { x: number; y: number },
	index: number,
): Promise<string> {
	const pdfUrl = await space.uploadMedia(file);
	const thumbBlob = await pdfFirstPageToBlob(file);
	const thumbnailUrl = thumbBlob ? await space.uploadMedia(thumbBlob) : pdfUrl;

	const thumbDims = thumbBlob ? await getBlobDimensions(thumbBlob) : null;
	const size = thumbDims
		? computeImageSize(thumbDims.width, thumbDims.height)
		: IMAGE_FALLBACK;

	const x = centre.x - size.width / 2 + index * STAGGER_OFFSET;
	const y = centre.y - size.height / 2 + index * STAGGER_OFFSET;

	const result = await space.createObject({
		data: {
			type: 'image',
			url: thumbnailUrl,
			sourceFile: pdfUrl,
			sourceFileName: file.name,
			sourceFileType: 'application/pdf',
			title: file.name.replace(/\.pdf$/i, ''),
			_x: x,
			_y: y,
			_width: size.width,
			_height: size.height,
		},
	});
	return result.object.id;
}

// -- Image -------------------------------------------------------------------

async function uploadImage(
	space: RoolChannel,
	file: File,
	centre: { x: number; y: number },
	index: number,
): Promise<string> {
	const dims = await getFileDimensions(file);
	const size = dims
		? computeImageSize(dims.width, dims.height)
		: IMAGE_FALLBACK;

	const x = centre.x - size.width / 2 + index * STAGGER_OFFSET;
	const y = centre.y - size.height / 2 + index * STAGGER_OFFSET;

	const mediaUrl = await space.uploadMedia(file);
	const result = await space.createObject({
		data: {
			type: 'image',
			url: mediaUrl,
			_x: x,
			_y: y,
			_width: size.width,
			_height: size.height,
		},
	});
	return result.object.id;
}
